/* eslint-disable prefer-reflect */
/* eslint-disable keyword-spacing */
/* eslint-disable space-before-function-paren */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import LytButton from '../library/button/LytButton';
import ButtonGroup from '../library/button-group/ButtonGroup';
import LytTextArea from '../library/text-area/LytTextArea';
import LytDropdown from '../library/dropdown/LytDropdown';
import LytMultiInput from '../library/multi-input/LytMultiInput';
import LytIcon from '../library/icon/LytIcon';
import { ICON_NAMES } from '../library/icon/icon-constants';
import LytModal from '../ui-core/modal/LytModal';
import { sendInvitationsOperation } from '../../routes/team/team.operations';
import { sendLytInvitationsOperation } from '../../routes/lyt-team-management/lyt-team.operations';
import { getUserAgencyId } from '../../services/requests/request.utils';
import { getUserRole } from '../../store/user/user.utils';
import { userRolesSelector } from '../../routes/users-and-permissions/roles/roles.selectors';
import {
    selectedAgencyJurisdictionsSelector,
    selectedAgencyTypeSelector,
} from '../../store/user-info/user-info.selectors';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';
import {
    ROLE_NAMES,
    LEGACY_ROLE_VALUES,
    AGENCY_TYPES,
} from '../../constants/roles.constants';
import {
    buildAssignableRolesList,
    formatRoleOptionLabel,
    getRoleSubtext,
    TEAM_CONTEXT,
} from './modal-role-options';
import { CalloutBanner } from './CalloutBanner';
export { TEAM_CONTEXT };

// ============================================================================
// CONSTANTS
// ============================================================================

// Role names — sourced from roles.constants.js (single source of truth)
const API_ROLES = ROLE_NAMES;
const LEGACY_ROLES = LEGACY_ROLE_VALUES;

/**
 * Role keywords for checking role types
 */
const ROLE_KEYWORDS = {
    ADMIN: 'admin',
    OWNER: 'owner',
    VIEWER: 'viewer',
};

/**
 * Context in which the Invite User Modal was opened.
 * Determines which roles LYT users can assign.
 *
 * AGENCY  – opened from an Agency Team page   → show agency roles
 * LYT     – opened from the LYT Team page     → show LYT-internal roles
 * (none)  – default / standalone               → minimal permissions
 */
/**
 * Roles that have permission to assign roles to invitees
 * Includes both API format and legacy format
 */
const ROLES_WITH_ASSIGN_PERMISSION = [
    // New API format
    API_ROLES.LYT_ADMIN,
    API_ROLES.LYT_EMPLOYEE,
    API_ROLES.TRANSIT_AGENCY_OWNER,
    API_ROLES.TRANSIT_AGENCY_ADMIN,
    API_ROLES.TRANSIT_AGENCY_VIEWER,
    API_ROLES.EMERGENCY_AGENCY_OWNER,
    API_ROLES.EMERGENCY_AGENCY_ADMIN,
    API_ROLES.EMERGENCY_AGENCY_VIEWER,
    API_ROLES.TRAFFIC_VIEWER,
    // Legacy format
    LEGACY_ROLES.LYT_ADMIN,
    LEGACY_ROLES.LYT_EMPLOYEE,
    LEGACY_ROLES.AGENCY_OWNER,
    LEGACY_ROLES.AGENCY_ADMIN,
    LEGACY_ROLES.AGENCY_VIEWER,
];

/**
 * Default role for new invitations
 */
const DEFAULT_ROLE = API_ROLES.TRANSIT_AGENCY_VIEWER;

// Schema
const schema = yup.object().shape({
    emails: yup
        .array()
        .of(yup.string().email('Please enter valid email addresses'))
        .min(1, 'At least one email address is required'),
    message: yup.string().trim(),
    role: yup.string().trim(),
});

// Styled Components
const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
const RequiredNote = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: ${({ theme }) => theme?.text?.default};
`;
const RequiredAsterisk = styled.span`
    color: ${({ theme }) => theme?.text?.danger?.default};
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
`;
const CalloutIcon = styled.div`
    display: flex;
    align-items: center;
    padding-top: 1px;
    flex-shrink: 0;
    color: ${({ theme }) => theme?.colors?.Blue4 || '#0066cc'};
`;
const CalloutText = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
    flex: 1;
`;
const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 24px;
`;
const FormSectionTitle = styled.h3`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_2_BOLD)}
    letter-spacing: 0.32px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const FormSectionDescription = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const FieldLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 4px;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_MEDIUM)}
    color: ${({ theme }) => theme?.text?.subtle};
    padding-bottom: 4px;
`;
const ErrorText = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme?.text?.danger?.default};
    margin-top: 4px;
`;
const RoleHintText = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme?.text?.subtle};
    margin: 4px 0 0 0;
`;
const FullWidthField = styled.div`
    width: 100%;

    /* Make text area full width */
    textarea {
        width: 100%;
    }
`;
const DropdownWrapper = styled.div`
    /* Custom dropdown styling for modal */
    [class*='dropdown'],
    [class*='Dropdown'] {
        border: 2px solid ${({ theme }) => theme?.colors?.Neutral3 || '#e0e0e0'};
        background-color: ${({ theme }) =>
            theme?.colors?.Neutral0 || '#ffffff'};
        border-radius: 4px;
    }
`;

/**
 * Determines if the current user can assign roles to invitees
 * @param {string} userRole - The current user's role
 * @returns {boolean} - True if user can assign roles
 */
const canAssignRoles = (userRole) => {
    return ROLES_WITH_ASSIGN_PERMISSION.includes(userRole);
};

/**
 * Checks if a role ID represents an admin or owner role
 * @param {string} roleId - The role ID to check
 * @returns {boolean} - True if role is admin or owner
 */
const isAdminOrOwnerRole = (roleId) => {
    return (
        roleId?.includes(ROLE_KEYWORDS.ADMIN) ||
        roleId?.includes(ROLE_KEYWORDS.OWNER)
    );
};

/**
 * Invite User Modal Component
 *
 * Two variants based on user role:
 * - Viewer: Can only input email and personal message (no role assignment)
 * - Admin/Owner: Can input email, assign role, and add personal message
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.closeModal - Function to close the modal with result, provided by ModalProvider
 */
const InviteUserModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
    teamContext = undefined,
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedJurisdictions, setSelectedJurisdictions] = useState({});
    const [emails, setEmails] = useState([]);
    const [emailError, setEmailError] = useState('');
    const { list: userRolesList } = useSelector(userRolesSelector);
    const agencyType = useSelector(selectedAgencyTypeSelector);
    const thisUserRole = getUserRole();
    const agencySelected = getUserAgencyId();

    // Jurisdictions for the user's agency (derived from sidebar data).
    // The sidebar fetches GET /agencies/cities/user which returns only the
    // jurisdictions assigned to the current user. For owners/admins this is
    // all agency jurisdictions; for traffic viewers it's their personal subset.
    const { list: jurisdictions, loading: jurisdictionsLoading } = useSelector(
        selectedAgencyJurisdictionsSelector
    );
    const userCanAssignRoles = canAssignRoles(thisUserRole);

    // Check if multiple emails are selected (admin roles should be disabled)
    const hasMultipleEmails = emails.length > 1;

    // Check if traffic_viewer is selected (jurisdiction assignment only for this role)
    const isTrafficViewerSelected =
        selectedRole?.id === API_ROLES.TRAFFIC_VIEWER;

    // Transform jurisdictions for dropdown
    const jurisdictionOptions = (jurisdictions || []).map((jurisdiction) => ({
        id: jurisdiction.id,
        text: jurisdiction.name,
    }));
    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            emails: [],
            message: '',
            role: DEFAULT_ROLE,
        },
        resolver: yupResolver(schema),
        mode: 'onChange',
    });
    const isSubmitDisabled = emails.length === 0;

    /**
     * Email validation regex
     */
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    /**
     * Handles email selection changes from LytMultiSelect
     */
    const handleEmailsChange = (selectedEmails) => {
        setEmails(selectedEmails);
        setValue('emails', selectedEmails);
        if (emailError) setEmailError(''); // Clear server validation error on change
    };
    const onSubmit = async (data) => {
        setIsLoading(true);
        setEmailError('');

        // Traffic viewers manually pick jurisdictions; all other roles
        // should pass through the full set of agency jurisdictions so the
        // backend can associate the invited user with the correct cities.
        const jurisdictionIds = isTrafficViewerSelected
            ? Object.keys(selectedJurisdictions)
            : (jurisdictions || []).map((j) => j.id);
        const isLytContext = teamContext === TEAM_CONTEXT.LYT;
        const result = isLytContext
            ? await dispatch(
                  sendLytInvitationsOperation({
                      recipients: emails,
                      assigned_role: selectedRole?.id || DEFAULT_ROLE,
                      customized_message: data.message || '',
                  })
              )
            : await dispatch(
                  sendInvitationsOperation({
                      recipients: emails,
                      assigned_role: selectedRole?.id || DEFAULT_ROLE,
                      agency_id: agencySelected,
                      jurisdictions: jurisdictionIds,
                      customized_message: data.message || '',
                  })
              );
        setIsLoading(false);

        // If validation error (e.g. invalid email), stay on modal and show error
        if (result?.validationError) {
            // 422 — invitee already has an account or a pending invitation.
            if (result.alreadyExists) {
                setEmailError(
                    'One or more of these users already have an account or a pending invitation.'
                );
                return; // Keep modal open
            }
            const emailErrors =
                result.data?.errors
                    ?.filter(
                        (e) =>
                            e.field?.includes('email') ||
                            e.messages?.some((m) => m.includes('email'))
                    )
                    ?.flatMap((e) => e.messages) || [];
            setEmailError(
                emailErrors.length > 0
                    ? emailErrors.join('. ')
                    : 'Please check the email addresses and try again.'
            );
            return; // Keep modal open
        }

        // For success or non-validation errors, close the modal
        closeModal({
            success: result?.success ?? false,
        });
    };
    const createRolesList = () => {
        const addDisabledState = (role) => {
            const isAdmin = isAdminOrOwnerRole(role.id);
            return {
                ...role,
                disabled: hasMultipleEmails && isAdmin,
                tooltip:
                    hasMultipleEmails && isAdmin
                        ? 'Admin roles can only be assigned to one user at a time'
                        : undefined,
            };
        };
        const allowedRoles = buildAssignableRolesList({
            userRolesList,
            currentUserRole: thisUserRole,
            teamContext,
            agencyType,
        });
        return allowedRoles.map((role) =>
            addDisabledState({
                id: role.role_name,
                text: formatRoleOptionLabel(role.role_name, teamContext),
                subtext: getRoleSubtext(role.role_name),
            })
        );
    };
    const rolesForSelect = createRolesList();
    const handleRoleChange = (selected) => {
        if (selected && Object.keys(selected).length > 0) {
            const selectedId = Object.keys(selected)[0];
            const role = rolesForSelect.find((r) => r.id === selectedId);
            setSelectedRole(role);
            setValue('role', selectedId);
        }
    };
    const handleCancel = () => {
        closeModal({
            cancelled: true,
        });
    };
    const footerActions = (
        <ButtonGroup>
            <LytButton onClick={handleCancel} type="tertiary">
                Cancel
            </LytButton>
            <LytButton
                onClick={handleSubmit(onSubmit)}
                type="primary"
                loading={isLoading}
                disabled={isSubmitDisabled}>
                Send Invite
            </LytButton>
        </ButtonGroup>
    );
    return (
        <LytModal
            open={open}
            title="Invite a New User"
            onClose={onClose}
            ariaLabelledBy="invite-user-title"
            ariaDescribedBy="invite-user-desc"
            size="medium"
            actions={footerActions}>
            <ModalContent>
                {/* Required fields note */}
                <RequiredNote id="invite-user-desc">
                    Required fields are marked
                    <RequiredAsterisk>*</RequiredAsterisk>
                </RequiredNote>

                {/* Info Callout Banner */}
                <CalloutBanner>
                    <CalloutIcon>
                        <LytIcon name={ICON_NAMES.CircleInformationFill} />
                    </CalloutIcon>
                    <CalloutText>
                        Invited users must accept the invitation and create an
                        account before they can access the organization.
                    </CalloutText>
                </CalloutBanner>

                {/* Email Section */}
                <FormSection>
                    <FormSectionTitle>Add Recipient</FormSectionTitle>
                    <FormSectionDescription>
                        Add the email addresses of the people you want to invite
                    </FormSectionDescription>
                    <div>
                        <FieldLabel>
                            Email Address<RequiredAsterisk>*</RequiredAsterisk>
                        </FieldLabel>
                        <LytMultiInput
                            id="invite-emails"
                            placeholder="Type email and press Enter to add"
                            values={emails}
                            onChange={handleEmailsChange}
                            validateValue={isValidEmail}
                            validationErrorText="Please enter a valid email address"
                        />
                        {errors.emails && (
                            <ErrorText>{errors.emails.message}</ErrorText>
                        )}
                        {emailError && <ErrorText>{emailError}</ErrorText>}
                    </div>
                </FormSection>

                {/* Role Section - Only shown for admin/owner users */}
                {userCanAssignRoles && rolesForSelect.length > 0 && (
                    <FormSection>
                        <div>
                            <FormSectionTitle>Assign a Role</FormSectionTitle>
                            <FormSectionDescription>
                                Select the role this user should have. Choose
                                the role that best matches the level of access
                                or permissions they need, Include a short note
                                to provide context or instructions to the
                                recipient.
                            </FormSectionDescription>
                        </div>
                        <div>
                            <DropdownWrapper>
                                <LytDropdown
                                    id="invite-role"
                                    name="role"
                                    dropdownPlaceholder="Select Role"
                                    options={rolesForSelect}
                                    selectedOptions={
                                        selectedRole
                                            ? {
                                                  [selectedRole.id]: true,
                                              }
                                            : {}
                                    }
                                    onChange={handleRoleChange}
                                    showSelectionIndicator
                                    fullWidth
                                />
                            </DropdownWrapper>
                            {selectedRole && (
                                <RoleHintText>
                                    {isAdminOrOwnerRole(selectedRole.id)
                                        ? 'Admins have full access, including user management'
                                        : selectedRole.id?.includes(
                                              ROLE_KEYWORDS.VIEWER
                                          )
                                        ? 'Viewers have read-only access to data'
                                        : ''}
                                </RoleHintText>
                            )}
                        </div>
                    </FormSection>
                )}

                {/* Jurisdiction Section - Only shown when traffic_viewer role is selected and jurisdictions exist */}
                {userCanAssignRoles &&
                    isTrafficViewerSelected &&
                    jurisdictionOptions.length > 0 && (
                        <FormSection>
                            <div>
                                <FormSectionTitle>
                                    Assign Jurisdiction(s)
                                </FormSectionTitle>
                                <FormSectionDescription>
                                    Assign a jurisdiction to limit the data a
                                    user can view.
                                </FormSectionDescription>
                            </div>
                            <DropdownWrapper>
                                <LytDropdown
                                    id="invite-jurisdiction"
                                    name="jurisdiction"
                                    dropdownPlaceholder={
                                        jurisdictionsLoading
                                            ? 'Loading jurisdictions...'
                                            : 'Select Jurisdiction(s)'
                                    }
                                    options={jurisdictionOptions}
                                    selectedOptions={selectedJurisdictions}
                                    onChange={(selected) =>
                                        setSelectedJurisdictions(selected)
                                    }
                                    loading={jurisdictionsLoading}
                                    multiselect
                                    showSelectionIndicator
                                    fullWidth
                                    usePortal
                                />
                            </DropdownWrapper>
                        </FormSection>
                    )}

                {/* Personal Message Section */}
                <FormSection>
                    <FormSectionTitle>Add a personal Message</FormSectionTitle>
                    <FormSectionDescription>
                        Include a short note to provide context or instructions
                        to the recipient.
                    </FormSectionDescription>
                    <FullWidthField>
                        <Controller
                            name="message"
                            control={control}
                            render={({ field }) => (
                                <LytTextArea
                                    {...field}
                                    id="invite-message"
                                    name="message"
                                    label="Message"
                                    placeholder="Message goes here"
                                    onChange={(value) => field.onChange(value)}
                                />
                            )}
                        />
                    </FullWidthField>
                </FormSection>
            </ModalContent>
        </LytModal>
    );
};
InviteUserModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
    teamContext: PropTypes.oneOf(['agency', 'lyt']),
};
export default InviteUserModal;
